import {
  Alert,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import AuthButton from "../components/auth/AuthButton";
import AuthFormContainer from "../components/auth/AuthFormContainer";
import AuthLink from "../components/auth/AuthLink";
import AuthTextField from "../components/auth/AuthTextField";
import { useLogin } from "../hooks/useAuth";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") ?? "";
  const login = useLogin();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const nextKind = next.startsWith("/party/play")
      ? "party_play"
      : next.startsWith("/purchase")
        ? "purchase"
        : next
          ? "other"
          : "none";
    trackEvent(MixpanelEvents.LOGIN_PAGE_VIEWED, {
      has_next: Boolean(next),
      next_kind: nextKind,
    });
  }, [next]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = t("auth.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("auth.emailInvalid");
    }

    if (!formData.password) {
      newErrors.password = t("auth.passwordRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    trackEvent(MixpanelEvents.LOGIN_SUBMIT, { has_next: Boolean(next) });
    login.mutate(formData);
  };

  return (
    <AuthFormContainer>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
      >
        <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          <Stack spacing={{ xs: 0.25, md: 0.5 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" } }}
            >
              {t("auth.login")}
            </Typography>
          </Stack>

          {login.error && (
            <Alert severity="error" sx={{ py: { xs: 0.5, md: 1 } }}>
              {(login.error as any)?.response?.data?.detail ||
                t("auth.invalidCredentials")}
            </Alert>
          )}

          <Stack spacing={{ xs: 1.5, md: 2 }}>
            <AuthTextField
              fullWidth
              type="email"
              label={t("auth.email")}
              autoComplete="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              error={!!errors.email}
              helperText={errors.email}
            />
            <AuthTextField
              fullWidth
              type="password"
              label={t("auth.password")}
              autoComplete="current-password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              error={!!errors.password}
              helperText={errors.password}
            />
          </Stack>

          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ py: { xs: 0.5, md: 0 } }}
          >
            <AuthLink to="/forgot-password">
              {t("auth.forgotPassword")}
            </AuthLink>
          </Stack>

          <AuthButton
            type="submit"
            variant="primary"
            loading={login.isPending}
          >
            {t("auth.login")}
          </AuthButton>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              textAlign: "center",
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              pt: { xs: 0.5, md: 0 }
            }}
          >
            {t("auth.dontHaveAccount")}{" "}
            <AuthLink to="/signup">
              {t("auth.signUp")}
            </AuthLink>
          </Typography>
        </Stack>
      </Box>
    </AuthFormContainer>
  );
};

export default LoginPage;
